import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Divider, IconButton, Typography } from '@mui/material';
import { AiSparkleIcon, CancelIcon } from '@sharedCR/coreDesignSystem/icons';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { AiProcessStepTerminalStatuses } from 'constants/aiProcessRuns';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useNowTick from 'hooks/useNowTick';

import {
  loadStepMessagesAction,
  selectFormByExecution,
  selectRunToolStats,
} from 'store/aiProcessRuns';
import {
  selectLoanNumber,
  selectPrimaryBorrowerCommonInfo,
} from 'store/loanApplications/selectors/loanApplicationCommonInfo.selectors';

import { AiActivityTestIds } from '../../../AiActivity.testIds';
import {
  deriveDisplayRunStatus,
  formatDuration,
  formatExactTime,
  formatNeedsInputLabel,
  formatTriggerInfo,
  getDescendantStepIds,
  getRunLiveCounts,
  getRunStepCounts,
  resolveDefaultStepId,
  resolveTriggerDisplayName,
} from '../../../AiActivity.utils';
import AiActivityCitationsProvider from '../../../AiActivityCitations/AiActivityCitationsProvider';
import StatusPill from '../../../StatusPill';
import TriggerAvatar from '../../../TriggerAvatar';
import {
  centerColumnStyles,
  contentStyles,
  emptyStateStyles,
  headerCloseButtonStyles,
  headerIdentityBadgeStyles,
  headerMetaBlockStyles,
  headerMetaLabelStyles,
  headerMetaStyles,
  headerMetaValueStyles,
  headerProgressSlotStyles,
  headerStripStyles,
  headerStyles,
  headerSubtitleStyles,
  headerTitleActionsStyles,
  headerTitleGroupStyles,
  headerTitleMainRowStyles,
  headerTitleRowStyles,
  masterDetailStyles,
} from '../../ExecutionDetailDrawer.styles';
import StepDetailPane from '../../StepDetailPane';
import FormReviewCard from '../../StepDetailPane/FormReviewCard';
import {
  countFilledFields,
  countSchemaFields,
} from '../../StepDetailPane/FormReviewCard/formReview.utils';
import StepNavRail from '../../StepNavRail';
import RunProgressMeter from '../RunProgressMeter';
import RunSnapshot from '../RunSnapshot';
import RunTimeline from '../RunTimeline';

import {
  HEADER_CLOSE_ICON_SIZE,
  HEADER_IDENTITY_ICON_SIZE,
  HEADER_SUBTITLE_TEST_ID,
} from './ExecutionDetailContent.constants';
import { buildExecutionSubtitle, resolveFormFillState } from './ExecutionDetailContent.utils';

const ExecutionDetailContent = ({ execution, processesById, steps, testId, onClose }) => {
  const dispatch = useDispatch();
  const [selectedStepId, setSelectedStepId] = useState(null);
  const userSelectedRef = useRef(false);
  const messagesFetchedStatusRef = useRef({});

  const selectedStep = useMemo(
    () => steps.find(step => step.id === selectedStepId) ?? null,
    [steps, selectedStepId],
  );

  // The form review card lazily loads this same entry; reading it here is a pure
  // subscription (no extra fetch) so the run snapshot can surface "Fields filled"
  // off the live form without prop-drilling through the step detail subtree.
  const form = useSelector(selectFormByExecution(execution?.id));
  const fieldCounts = useMemo(
    () => ({ filled: countFilledFields(form), total: countSchemaFields(form) }),
    [form],
  );

  // Run-wide tool stats are derived from every step's loaded messages; the
  // eager-load effect below pulls them in so these counts reflect the whole run.
  const toolStats = useSelector(selectRunToolStats(execution?.id));

  // The drawer lives in the loan editor, so the active loan application is
  // already loaded; these are pure subscriptions (no extra fetch) used to anchor
  // the header with the loan number and primary borrower.
  const loanNumber = useSelector(selectLoanNumber);
  const primaryBorrower = useSelector(selectPrimaryBorrowerCommonInfo);
  const headerSubtitle = useMemo(
    () => buildExecutionSubtitle({ loanNumber, primaryBorrower }),
    [loanNumber, primaryBorrower],
  );

  const retryFromHereDependentCount = useMemo(
    () => (_.isNil(selectedStepId) ? undefined : getDescendantStepIds(steps, selectedStepId).size),
    [steps, selectedStepId],
  );

  const onSelectStep = useCallback(stepId => {
    userSelectedRef.current = true;
    setSelectedStepId(stepId);
  }, []);

  // DEV-3055: tick once a second while the run is in-flight so the header's
  // running "Duration" advances smoothly instead of freezing between events.
  useNowTick(!_.isNil(execution) && !execution.completedAt);

  // Pick a default step the first time steps arrive, and re-pick only if the
  // current selection is gone. A manual selection (userSelectedRef) is never
  // overridden, and a still-valid selection is left alone so a streaming run
  // doesn't yank the pane out from under the reader.
  useEffectSafeStrictMode(() => {
    if (userSelectedRef.current || steps.length === 0) {
      return;
    }

    if (selectedStepId && steps.some(step => step.id === selectedStepId)) {
      return;
    }

    setSelectedStepId(resolveDefaultStepId(steps));
  }, [steps, selectedStepId]);

  useEffectSafeStrictMode(() => {
    if (_.isNil(selectedStepId)) {
      return;
    }

    dispatch(loadStepMessagesAction({ stepExecutionId: selectedStepId }));
  }, [dispatch, selectedStepId]);

  // Eager-load every step's messages so the run snapshot can count run-wide tool
  // activity (not just the selected step), and re-pull a step's transcript when
  // it reaches a terminal status.
  //
  // LEN-26: a step persists its tool calls + final output as it runs, so the
  // fire-once REST load captured an empty/partial transcript while the step was
  // still Running (the non-force load short-circuits on READY, so the later
  // status change never re-fetched). Live TOOL_*/PLAN_* SignalR events would
  // stream the deltas in, but process runs don't emit them, so the completed
  // step's tool activity stayed frozen until a full page refresh. Tracking the
  // status each step's messages were last loaded at lets us force ONE re-fetch
  // on the transition into a terminal status — surfacing the final tool calls
  // and output live instead of only after a manual refresh.
  useEffectSafeStrictMode(() => {
    steps.forEach(step => {
      const lastLoadedStatus = messagesFetchedStatusRef.current[step.id];

      if (lastLoadedStatus === step.status) {
        return;
      }

      const isTerminalTransition =
        !_.isNil(lastLoadedStatus) && AiProcessStepTerminalStatuses.has(step.status);

      messagesFetchedStatusRef.current[step.id] = step.status;
      dispatch(loadStepMessagesAction({ stepExecutionId: step.id, force: isTerminalTransition }));
    });
  }, [dispatch, steps]);

  const closeButton = (
    <IconButton
      disableRipple
      aria-label="Close"
      data-testid={AiActivityTestIds.EXECUTION_DRAWER_CLOSE}
      sx={headerCloseButtonStyles}
      onClick={onClose}
    >
      <CancelIcon size={HEADER_CLOSE_ICON_SIZE} />
    </IconButton>
  );

  if (_.isNil(execution)) {
    return (
      <Box data-testid={testId} sx={emptyStateStyles}>
        <Box sx={headerTitleActionsStyles}>{closeButton}</Box>
        <Typography variant="regular14">Loading execution…</Typography>
      </Box>
    );
  }

  const { runningCount, isNeedsInput } = getRunLiveCounts({ run: execution, steps });
  const statusLabel =
    isNeedsInput && runningCount > 0 ? formatNeedsInputLabel(runningCount) : undefined;
  const processName = processesById[execution.aiProcessId]?.name ?? '—';
  const displayStatus = deriveDisplayRunStatus(execution, steps);
  const stepCounts = getRunStepCounts(execution, _.map(steps, 'status'));
  const elapsedLabel = formatDuration(execution);
  const isRunComplete = !_.isNil(execution.completedAt);
  const durationLabel = isRunComplete ? 'Completed' : 'Elapsed';
  const durationValue = isRunComplete ? formatExactTime(execution.completedAt) : elapsedLabel;
  const hasSteps = steps.length > 0;
  const formFillState = resolveFormFillState({ isRunComplete, isNeedsInput, runningCount });

  return (
    <Box data-testid={testId} sx={contentStyles}>
      <Box sx={headerStyles}>
        <Box sx={headerTitleRowStyles}>
          <Box sx={headerTitleGroupStyles}>
            <Box sx={headerTitleMainRowStyles}>
              <Box sx={headerIdentityBadgeStyles}>
                <AiSparkleIcon size={HEADER_IDENTITY_ICON_SIZE} />
              </Box>
              <Typography variant="bold20">{processName}</Typography>
            </Box>
            {!_.isNil(headerSubtitle) && (
              <Typography
                color="text.secondary"
                data-testid={HEADER_SUBTITLE_TEST_ID}
                sx={headerSubtitleStyles}
                variant="regular12"
              >
                {headerSubtitle}
              </Typography>
            )}
          </Box>
          <Box sx={headerTitleActionsStyles}>
            <StatusPill label={statusLabel} status={displayStatus} />
            {closeButton}
          </Box>
        </Box>
        <Box sx={headerStripStyles}>
          <Box sx={headerMetaStyles}>
            <Box sx={headerMetaBlockStyles}>
              <Typography color="text.secondary" sx={headerMetaLabelStyles} variant="medium12">
                Trigger
              </Typography>
              <Box sx={headerMetaValueStyles}>
                <TriggerAvatar name={resolveTriggerDisplayName(execution)} />
                <Typography variant="regular12">{formatTriggerInfo(execution)}</Typography>
              </Box>
            </Box>
            <Box sx={headerMetaBlockStyles}>
              <Typography color="text.secondary" sx={headerMetaLabelStyles} variant="medium12">
                Started
              </Typography>
              <Box sx={headerMetaValueStyles}>
                <Typography variant="regular12">{formatExactTime(execution.startedAt)}</Typography>
              </Box>
            </Box>
            <Box sx={headerMetaBlockStyles}>
              <Typography color="text.secondary" sx={headerMetaLabelStyles} variant="medium12">
                {durationLabel}
              </Typography>
              <Box sx={headerMetaValueStyles}>
                <Typography variant="regular12">{durationValue}</Typography>
              </Box>
            </Box>
          </Box>
          {stepCounts.total > 0 && (
            <Box sx={headerProgressSlotStyles}>
              <RunProgressMeter
                completed={stepCounts.finished}
                isNeedsInput={isNeedsInput}
                ratio={stepCounts.ratio}
                runningCount={runningCount}
                status={displayStatus}
                total={stepCounts.total}
              />
            </Box>
          )}
        </Box>
      </Box>
      {steps.length > 1 && (
        <RunTimeline
          runCompletedAt={execution.completedAt}
          runStartedAt={execution.startedAt}
          steps={steps}
        />
      )}
      <Box sx={masterDetailStyles}>
        {hasSteps ? (
          <StepNavRail selectedStepId={selectedStepId} steps={steps} onSelect={onSelectStep} />
        ) : (
          <Box sx={emptyStateStyles}>
            <Typography variant="regular14">No steps yet.</Typography>
          </Box>
        )}
        <Divider flexItem orientation="vertical" />
        <Box sx={centerColumnStyles}>
          <AiActivityCitationsProvider executionId={execution.id}>
            <FormReviewCard executionId={execution.id} fillState={formFillState} />
            <StepDetailPane
              execution={execution}
              key={selectedStep?.id}
              retryFromHereDependentCount={retryFromHereDependentCount}
              step={selectedStep}
            />
          </AiActivityCitationsProvider>
        </Box>
        <Divider flexItem orientation="vertical" />
        <RunSnapshot
          agentsUsed={toolStats.agents}
          completed={stepCounts.finished}
          documentsReviewed={toolStats.documents}
          elapsedLabel={elapsedLabel}
          fieldsFilled={fieldCounts.filled}
          fieldsTotal={fieldCounts.total}
          guidelineLookups={toolStats.guideline}
          recordsRead={toolStats.recordsRead}
          recordsUpdated={toolStats.recordsUpdated}
          skillsUsed={toolStats.skills}
          toolCalls={toolStats.toolCalls}
          total={stepCounts.total}
        />
      </Box>
    </Box>
  );
};

ExecutionDetailContent.propTypes = {
  processesById: PropTypes.object.isRequired,
  steps: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClose: PropTypes.func.isRequired,
  execution: PropTypes.shape({
    id: PropTypes.string,
    aiProcessId: PropTypes.string,
    status: PropTypes.string,
    startedAt: PropTypes.string,
    completedAt: PropTypes.string,
    triggerSource: PropTypes.string,
    triggeredByUserId: PropTypes.string,
  }),
  testId: PropTypes.string,
};

export default memo(ExecutionDetailContent);
